import { useRef, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { MessageCircle, X, Menu, BookOpenIcon } from 'lucide-react'
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup
} from '@components/ui/resizable'

import { useBreakpoint } from '@contexts/breakpoints'
import { useCurrentUser } from '@contexts/currentUser'
import { EducatorProjectSubmissionChannelProvider } from '@contexts/educatorProjectSubmissionChannelContext'
import { useSubscription } from '../../../hooks/subscription'
import { useEducatorProjectSubmission } from './hooks'
import {
  ChatPlayground,
  ProjectBlocks
} from '@pages/EducatorProjectSubmission'
import Skeleton from '@pages/EducatorProjectSubmission/Skeleton'
import SubmitModal from './SubmitModal'
import LessonOutline from '@pages/EducatorProjectSubmission/LessonOutline'
import { PlainProgressBar } from '@pages/EducatorProjectSubmission/ProgressBar'
import NavTabs from '@components/NavTabs/NavTabs'
import { Routes, Route, useNavigate } from 'react-router-dom'
import InstantFeedbackProvider from '@pages/EducatorProjectSubmission/InstantFeedbackProvider'

const Preview = ({ id }) => {
  const contentPanelRef = useRef(null)
  const [currentBlockIndex, setCurrentBlockIndex] = useState(0)
  const [isSubmitModalOpen, setIsSubmitModalOpen] = useState(false)
  const [isChatCollapsed, setIsChatCollapsed] = useState(false)
  const { hasProFeatures } = useSubscription()
  const { project, submission, isLoading } = useEducatorProjectSubmission({ educatorProjectId: id })
  const { currentMembership: { organization: { id: organizationId } = {} } = {} } = useCurrentUser()
  const isMobile = useBreakpoint('sm')
  const navigate = useNavigate()

  const methods = useForm({ mode: 'onTouched', defaultValues: { message: null, attachments: [] } })

  const currentBlock = project?.blocks[currentBlockIndex]
  const currentBlockSubmission = submission?.blockSubmissions?.find(blockSubmission => blockSubmission.blockId === currentBlock?.blockId)
  const projectName = project?.name || 'Untitled lesson'
  const assignment = project?.series?.assignment

  if (isLoading || !project || !submission) return <Skeleton />

  const moveToNextBlock = () => {
    const nextIndex = currentBlockIndex + 1
    setCurrentBlockIndex(nextIndex)
  }

  const moveToPreviousBlock = () => {
    setCurrentBlockIndex(currentBlockIndex - 1)
  }

  const setBlockIndex = (index) => {
    setCurrentBlockIndex(index)
    if (isMobile) {
      navigate('lesson')
    }
  }

  const toggleChatCollapse = () => {
    setIsChatCollapsed(!isChatCollapsed)
  }

  return (
    <EducatorProjectSubmissionChannelProvider
      submissionId={submission.id}
      organizationId={organizationId}
    >
      <InstantFeedbackProvider project={project} submission={submission}>
        {({ setBlockFeedbackLoading, getBlockFeedbackState }) => (
          <div className='h-full flex flex-col max-h-full w-full min-h-full'>
            <Choose>
              <When condition={isMobile}>
                <div className='h-[calc(100%-64px)] flex flex-col'>

                  <NavTabs className='sticky top-0'>
                    <NavTabs.Link
                      to=''
                      end
                      icon={<Menu size={16} />}
                      label='outline'
                    />
                    <NavTabs.Link
                      to='lesson'
                      end
                      icon={<BookOpenIcon size={16} />}
                      label='Lesson'
                    />
                    {project?.chatEnabled && (
                      <NavTabs.Link
                        to='chat'
                        end
                        icon={<MessageCircle size={16} />}
                        label='Chat'
                      />
                    )}
                  </NavTabs>

                  <div className='h-[calc(100%-40px)] overflow-auto'>
                    <Routes>
                      <Route
                        path=""
                        element={
                          <LessonOutline
                            projectName={projectName}
                            blocks={project.blocks}
                            blockSubmissions={submission.blockSubmissions}
                            currentBlockIndex={currentBlockIndex}
                            setCurrentBlockIndex={setBlockIndex}
                            getBlockFeedbackState={getBlockFeedbackState}
                          />
                        }
                      />
                      <Route
                        path="lesson"
                        element={
                          <div ref={contentPanelRef} className='pb-4 flex flex-col h-full overflow-y-auto'>
                            <ProjectBlocks
                              blocks={project.blocks}
                              submissionId={submission.id}
                              assignmentId={assignment?.id}
                              submissionStatus={submission.status}
                              currentBlock={currentBlock}
                              currentBlockSubmission={currentBlockSubmission}
                              currentBlockIndex={currentBlockIndex}
                              moveToNextBlock={moveToNextBlock}
                              moveToPreviousBlock={moveToPreviousBlock}
                              isSubmissionClosed={false}
                              setIsSubmitModalOpen={setIsSubmitModalOpen}
                              isTimeUp={false}
                              feedbackType={project?.feedbackType}
                              setBlockFeedbackLoading={setBlockFeedbackLoading}
                              getBlockFeedbackState={getBlockFeedbackState}
                            />
                            <PlainProgressBar
                              blocks={project.blocks}
                              blockSubmissions={submission.blockSubmissions}
                              className='absolute bottom-0 left-0 w-full'
                            />
                          </div>
                        }
                      />
                      <Route
                        path="chat"
                        element={
                          project?.chatEnabled && (
                            <div className='h-full w-full bg-background'>
                              <FormProvider {...methods}>
                                <ChatPlayground
                                  submissionId={submission.id}
                                  isSubmissionClosed={false}
                                  proFeaturesEnabled={hasProFeatures}
                                  submissionStatus={submission.status}
                                />
                              </FormProvider>
                            </div>
                          )
                        }
                      />
                    </Routes>
                  </div>
                </div>
              </When>

              <Otherwise>
                <ResizablePanelGroup
                  className='flex grow bg-background max-h-[calc(100vh-55px)]'
                  style={{ overflow: 'auto' }}
                  direction='horizontal'
                  id='studentProjectPlayground'
                >
                  <ResizablePanel defaultSize={20} minSize={15} maxSize={30} className='flex flex-col bg-background max-h-[calc(100vh-60px)] overflow-y-scroll'>
                    <LessonOutline
                      projectName={projectName}
                      blocks={project.blocks}
                      blockSubmissions={submission.blockSubmissions}
                      currentBlockIndex={currentBlockIndex}
                      setCurrentBlockIndex={setBlockIndex}
                      getBlockFeedbackState={getBlockFeedbackState}
                    />
                  </ResizablePanel>
                  <ResizableHandle withHandle />
                  <ResizablePanel
                    defaultSize={isChatCollapsed || !project?.chatEnabled ? 80 : 50}
                    className='flex flex-col max-h-full'
                  >
                    <div className='h-full' ref={contentPanelRef}>
                      <ProjectBlocks
                        blocks={project.blocks}
                        submissionId={submission.id}
                        assignmentId={assignment?.id}
                        submissionStatus={submission.status}
                        currentBlock={currentBlock}
                        currentBlockSubmission={currentBlockSubmission}
                        currentBlockIndex={currentBlockIndex}
                        moveToNextBlock={moveToNextBlock}
                        moveToPreviousBlock={moveToPreviousBlock}
                        isSubmissionClosed={false}
                        setIsSubmitModalOpen={setIsSubmitModalOpen}
                        isTimeUp={false}
                        feedbackType={project?.feedbackType}
                        setBlockFeedbackLoading={setBlockFeedbackLoading}
                        getBlockFeedbackState={getBlockFeedbackState}
                      />
                    </div>
                  </ResizablePanel>

                  <If condition={project?.chatEnabled && !isChatCollapsed}>
                    <ResizableHandle withHandle />
                    <ResizablePanel
                      defaultSize={30}
                      minSize={25}
                      className='relative overflow-auto'
                    >
                      <div className="absolute top-2 right-2 z-20">
                        <button
                          onClick={toggleChatCollapse}
                          className="p-2 rounded-sm bg-background hover:bg-accent"
                          aria-label="Collapse chat panel"
                        >
                          <X size={16} />
                        </button>
                      </div>
                      <FormProvider {...methods}>
                        <ChatPlayground
                          submissionId={submission.id}
                          isSubmissionClosed={false}
                          proFeaturesEnabled={hasProFeatures}
                          submissionStatus={submission.status}
                        />
                      </FormProvider>
                    </ResizablePanel>
                  </If>

                  <If condition={project?.chatEnabled && isChatCollapsed}>
                    <div className="flex justify-center ml-1">
                      <button
                        onClick={toggleChatCollapse}
                        className="p-2 bg-background border-l border-border shadow-sm hover:bg-accent"
                        aria-label="Expand chat panel"
                      >
                        <MessageCircle size={16} />
                      </button>
                    </div>
                  </If>
                </ResizablePanelGroup>
              </Otherwise>
            </Choose>

            <SubmitModal
              closeModal={() => setIsSubmitModalOpen(false)}
              isOpen={isSubmitModalOpen}
            />
          </div>
        )}
      </InstantFeedbackProvider>
    </EducatorProjectSubmissionChannelProvider>
  )
}

export default Preview
